/**
 * An organization's brand as the portal's components read it: its published logos and colors,
 * light and dark (TenantBrandingProvider carries it).
 */

import { hexToOklch } from "@/design-system/presets";
import { isValidHexColor } from "@/utils/is-valid-hex-color";
import type { BrandImageSlot } from "@convex/tables/brandKits";

export interface BrandConfig {
    // Light mode
    logoUrl: string | null;
    primaryColor: string;
    accentColor: string;

    // Dark mode
    logoDarkUrl: string | null;
    primaryColorDark: string;
    accentColorDark: string;
}

// MERGE-GRAFT (2026-08-02): main-next addition restored during the epic->main-next merge.
/** The platform's own name. Every surface that has to print it — the page
 *  titles, the platform wordmark, the transactional email sender — reads this
 *  rather than spelling it out. */
export const PLATFORM_NAME = "DisputeWell";

/** The platform's own images, the one place each is named (docs/brand/brand-assets.md). Every
 *  surface that shows DisputeWell's logo or icon rather than an organization's reads it here. */
export const DISPUTEWELL_PLATFORM_BRAND = {
    name: PLATFORM_NAME,
    /** The wordmark, for light surfaces. */
    logoUrl: "/logos/disputewell-logo-v27-light.svg",
    /** The wordmark, for dark surfaces. */
    logoDarkUrl: "/logos/disputewell-logo-v27-dark.svg",
    /** The wordmark as a PNG, for an email or a crawler that can't take an SVG. */
    logoRasterUrl: "/logos/disputewell-logo-v27-light.png",
    /** The square icon: the browser tab and the home screen. */
    iconUrl: "/icon/icon.svg",
} as const;

/** The optional hook each of the worn brand kit's AI logos goes in (registry.css's FluidMark block):
 *  light mode's, and dark mode's, which falls back to the kit's light-mode logo. A brand kit's slots
 *  are convex/tables/brandKits.ts's. */
export const FLUID_MARK_KIT_PROPERTIES: Partial<Record<BrandImageSlot, string>> = {
    fluidMark: "--dw-fluid-mark-kit",
    fluidMarkDark: "--dw-fluid-mark-dark-kit",
};

// Default brand colors (DisputeWell orange and sky blue)
const DEFAULT_BRAND: BrandConfig = {
    // Light mode
    logoUrl: null,
    primaryColor: "#f97316",
    accentColor: "#0ea5e9",

    // Dark mode (defaults to same as light mode)
    logoDarkUrl: null,
    primaryColorDark: "#f97316",
    accentColorDark: "#0ea5e9",
};

/**
 * Ensure a color is a valid hex, falling back to default if not.
 */
export function normalizeColor(color: string | null | undefined, fallback: string): string {
    if (!color) {
        return fallback;
    }
    if (isValidHexColor(color)) {
        return color;
    }
    // Try adding # if missing
    const withHash = `#${color}`;
    if (isValidHexColor(withHash)) {
        return withHash;
    }
    return fallback;
}

/**
 * Build a complete BrandConfig from database values.
 * Dark mode values fallback to light mode values if not provided.
 */
export function buildBrandConfig(logoUrl: string | null | undefined, primaryColor: string | null | undefined, accentColor: string | null | undefined, logoDarkUrl?: string | null | undefined, primaryColorDark?: string | null | undefined, accentColorDark?: string | null | undefined): BrandConfig {
    // Normalize light mode colors
    const normalizedPrimary = normalizeColor(primaryColor, DEFAULT_BRAND.primaryColor);
    const normalizedAccent = normalizeColor(accentColor, DEFAULT_BRAND.accentColor);

    // Normalize dark mode colors (fallback to light mode if not provided)
    const normalizedPrimaryDark = normalizeColor(primaryColorDark, normalizedPrimary);
    const normalizedAccentDark = normalizeColor(accentColorDark, normalizedAccent);

    return {
        // Light mode
        logoUrl: logoUrl || null,
        primaryColor: normalizedPrimary,
        accentColor: normalizedAccent,

        // Dark logo falls back to light logo. If neither is set, both are null
        // (UI shows initials/default icon). `logoDarkUrl || logoUrl` is intentional:
        // an org that only uploads a light logo gets it in both modes automatically.
        logoDarkUrl: logoDarkUrl || logoUrl || null,
        primaryColorDark: normalizedPrimaryDark,
        accentColorDark: normalizedAccentDark,
    };
}

/**
 * The org identity a password setup/reset token resolves to (D-088 white-label).
 * Produced by convex/betterAuth.resolvePasswordSetupToken and delivered through
 * /api/portal/setup-token-info; null at the consumer means the platform brand
 * (token unresolvable, or ambiguous across orgs). `showAttribution` carries the
 * plan-derived white-label tier: basic keeps the "Powered by DisputeWell" line
 * on org-branded surfaces, full omits it.
 */
export interface TokenBranding {
    name: string;
    slug: string;
    logoUrl: string | null;
    logoDarkUrl: string | null;
    primaryColor: string | null;
    accentColor: string | null;
    primaryColorDark: string | null;
    accentColorDark: string | null;
    showAttribution: boolean;
}
/**
 * Generate OKLCH CSS custom properties for a brand config.
 * Used for OKLCH-based theming instead of HSL.
 */
export function generateBrandCssVarsOklch(brand: BrandConfig): Record<string, string> {
    const oklchCss = (hex: string) => {
        const { L, C, H } = hexToOklch(hex);
        return `oklch(${Math.round(L * 1000) / 1000} ${Math.round(C * 1000) / 1000} ${Math.round(H * 10) / 10})`;
    };
    return {
        // Light mode (OKLCH)
        "--brand-primary-oklch": oklchCss(brand.primaryColor),
        "--brand-accent-oklch": oklchCss(brand.accentColor),
        "--brand-primary-hex": brand.primaryColor,
        "--brand-accent-hex": brand.accentColor,

        // Dark mode (OKLCH)
        "--brand-primary-dark-oklch": oklchCss(brand.primaryColorDark),
        "--brand-accent-dark-oklch": oklchCss(brand.accentColorDark),
        "--brand-primary-dark-hex": brand.primaryColorDark,
        "--brand-accent-dark-hex": brand.accentColorDark,
    };
}
